import { forwardRef } from 'react';
import { CanvasLayer } from './CanvasLayer';
import { SwatchRow } from './SwatchRow';
import { CANVAS_SIZE, buildBackgroundCSS } from '../lib/canvas-utils';

export const CanvasArea = forwardRef(function CanvasArea(
  { layers, background, selectedId, onSelect, onChange, onDeselect, onInteractionStart, onSetTint },
  ref
) {
  const isTransparent = background.type === 'transparent';
  const bgStyle = buildBackgroundCSS(background);
  const selectedLayer = layers.find((l) => l.id === selectedId);

  return (
    <div
      data-testid="canvas-area-wrapper"
      className="relative flex-1 flex items-center justify-center p-2 sm:p-6 lg:p-10 dotgrid-bg overflow-hidden"
      onPointerDown={onDeselect}
    >
      {selectedLayer?.tint && (
        <div
          data-testid="canvas-tint-bar"
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 border-2 border-ink bg-paper shadow-brutSm px-3 py-1.5"
        >
          <span className="font-outfit text-[10px] font-bold uppercase tracking-wider">Colour</span>
          <SwatchRow
            value={selectedLayer.tint}
            onChange={(t) => onSetTint(selectedLayer.id, t)}
            testIdPrefix="canvas-tint"
            size="lg"
          />
        </div>
      )}
      <div
        className="relative"
        style={{
          width: 'min(calc(100vh - 200px), 100%, 92vw)',
          aspectRatio: '1 / 1',
        }}
      >
        {/* Outer wrapper shows checker pattern when canvas is transparent */}
        <div
          className={`absolute inset-0 border-2 border-ink shadow-brutXl ${isTransparent ? 'checker-bg' : ''}`}
        >
          {/* The actual exportable canvas */}
          <div
            ref={ref}
            data-testid="canvas-root"
            className="relative w-full h-full overflow-hidden"
            style={{
              ...bgStyle,
              // The element rendered at responsive size; pixelRatio used at export time
              // to capture at exact CANVAS_SIZE x CANVAS_SIZE.
            }}
            onPointerDown={(e) => {
              // pointer down on bare canvas deselects
              if (e.target === e.currentTarget) onDeselect();
            }}
          >
            {layers
              .slice()
              .sort((a, b) => a.zIndex - b.zIndex)
              .map((layer) => (
                <CanvasLayer
                  key={layer.id}
                  layer={layer}
                  selected={layer.id === selectedId}
                  canvasRef={ref}
                  onSelect={onSelect}
                  onChange={onChange}
                  onInteractionStart={onInteractionStart}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
});

// Keep CANVAS_SIZE accessible
export { CANVAS_SIZE };
